import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { api, errText } from "@/lib/api";
import { getToken, useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { AuthShell } from "@/components/AuthShell";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { refresh } = useAuth();
  const [state, setState] = useState(token ? "pending" : "error");
  const [msg, setMsg] = useState(token ? "" : "This link is incomplete. Open it from your email again.");
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    api.post("/auth/verify-email", { token })
      .then(() => { setState("ok"); if (getToken()) refresh(); })
      .catch((err) => { setState("error"); setMsg(errText(err, "This link is invalid or has expired.")); });
  }, [token, refresh]);

  const signedIn = Boolean(getToken());

  return (
    <AuthShell label="Email confirmation" sub="Confirming your address lets us reach you about your documents, plan and shared videos."
      heading={<>Almost<br />there.</>}>
      {state === "pending" && (
        <p data-testid="verify-pending" className="text-sm uppercase tracking-widest text-muted-foreground">Confirming your email…</p>
      )}
      {state === "ok" && (
        <div data-testid="verify-success" className="space-y-5">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-6 h-6 text-primary shrink-0 mt-0.5" />
            <div>
              <h2 className="font-serif-display text-3xl mb-2">Email confirmed</h2>
              <p className="text-sm text-muted-foreground">Thanks — your address is verified and your account is all set.</p>
            </div>
          </div>
          <Button asChild data-testid="verify-continue" className="w-full h-11 bg-foreground text-background hover:bg-primary rounded-sm btn-invert">
            <Link to={signedIn ? "/app" : "/login"}>{signedIn ? "Go to my documents →" : "Log in →"}</Link>
          </Button>
        </div>
      )}
      {state === "error" && (
        <div data-testid="verify-error" className="space-y-5">
          <div className="flex items-start gap-3">
            <XCircle className="w-6 h-6 text-destructive shrink-0 mt-0.5" />
            <div>
              <h2 className="font-serif-display text-3xl mb-2">Link didn't work</h2>
              <p className="text-sm text-muted-foreground">{msg}</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            {signedIn ? <>Open <Link to="/app" className="underline" data-testid="verify-go-app">your documents</Link> and use <strong>Resend link</strong> in the banner to get a fresh one.</>
              : <><Link to="/login" className="underline" data-testid="verify-go-login">Log in</Link> and use <strong>Resend link</strong> in the banner to get a fresh one.</>}
          </p>
        </div>
      )}
    </AuthShell>
  );
}
